import * as React from 'react';

import { pickBy } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { CustomLabels, FormattingOptions, ReplaceCustomContent } from 'bundles/custom-labels/types/CustomLabels';
import {
  capitalizeEachWord,
  getCustomCourseTerm,
  getCustomWeekTerm,
  isPluralSupportedWeekTerm,
} from 'bundles/custom-labels/utils/customLabelsMapping';
import { useIsWeekToModuleEnabledForCourse } from 'bundles/custom-labels/utils/featureFlags';

import _t from 'i18n!nls/custom-labels';

export type OutputProps = {
  replaceCustomContent: ReplaceCustomContent;
};

export type UseCustomLabelsMappingOptions = {
  /** When true, skip the WeekToModuleStatus query (e.g. when enableCustomLabels is false). */
  skipWeekToModuleQuery?: boolean;
  /** When skipWeekToModuleQuery is true, use this as isModuleDefault (e.g. true for "Module" terminology). */
  isModuleDefaultWhenSkipped?: boolean;
};

export const useCustomLabelsMapping = (
  courseId: string,
  customLabels?: CustomLabels,
  mappingOptions?: UseCustomLabelsMappingOptions
): OutputProps => {
  const isModuleDefault = useIsWeekToModuleEnabledForCourse(courseId, {
    skipQuery: mappingOptions?.skipWeekToModuleQuery,
    defaultValueWhenSkipped: mappingOptions?.isModuleDefaultWhenSkipped,
  });
  const defaultCourseTerm = getCustomCourseTerm();
  const defaultCoursesTerm = getCustomCourseTerm(undefined, undefined, true);
  const defaultWeekTerm = getCustomWeekTerm(isModuleDefault);
  const defaultWeeksTerm = getCustomWeekTerm(isModuleDefault, undefined, undefined, true);
  const defaultCapitalizedCourseTerm = capitalizeEachWord(defaultCourseTerm);
  const defaultCapitalizedCoursesTerm = capitalizeEachWord(defaultCoursesTerm);
  const defaultCapitalizedWeekTerm = capitalizeEachWord(defaultWeekTerm);
  const defaultCapitalizedWeeksTerm = capitalizeEachWord(defaultWeeksTerm);

  const getNumberedWeekTerms = (weekNumber: number) => {
    // TODO LRNENG-3369 Update these from week to module upon graduating the week to module project
    // In the meantime, custom-labels v2 will be adjusting the namings on the backend so no FE logic is required
    let weekWithNumber = isModuleDefault
      ? _t('module #{weekNumber}', {
          weekNumber,
        })
      : _t('week #{weekNumber}', {
          weekNumber,
        });

    let capitalizedWeekWithNumber = isModuleDefault
      ? _t('Module #{weekNumber}', {
          weekNumber,
        })
      : _t('Week #{weekNumber}', {
          weekNumber,
        });

    if (customLabels) {
      const { namings, moduleNames, isRhyme } = customLabels;
      const { courseraWeek } = namings;

      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeek);

      if (isRhyme) {
        weekWithNumber = courseraWeek ?? weekWithNumber;
        capitalizedWeekWithNumber = courseraWeekWithCapitalization ?? capitalizedWeekWithNumber;
      } else if (moduleNames) {
        const { moduleName } = moduleNames.find(({ weekNumber: week }) => week === weekNumber) ?? {};

        if (moduleName) {
          weekWithNumber = moduleName ?? weekWithNumber;
          capitalizedWeekWithNumber = moduleName ?? capitalizedWeekWithNumber;
        }
      } else {
        weekWithNumber = _t('#{courseraWeek} #{weekNumber}', {
          courseraWeek: courseraWeek ?? defaultWeekTerm,
          weekNumber,
        });
        capitalizedWeekWithNumber = _t('#{courseraWeekWithCapitalization} #{weekNumber}', {
          courseraWeekWithCapitalization: courseraWeekWithCapitalization ?? defaultCapitalizedWeekTerm,
          weekNumber,
        });
      }
    }

    return {
      weekWithNumber,
      capitalizedWeekWithNumber,
    };
  };

  // Provide a replacement function as a prop to be used by consumers.
  //
  // Usage notes:
  // - Pass `returnsString: true` when you need a string value (for props like `title`/`subtitle`).
  //   Omit it to get a JSX element via `FormattedMessage`.
  // - If your `_t(...)` call does not interpolate variables, either of these is fine:
  //   `replaceCustomContent(_t('Continue to #{week}'), { returnsString: true })`
  //   `replaceCustomContent('Continue to #{week}', { returnsString: true })`
  // - If your `_t(...)` call needs interpolation values, pass those values in `additionalVariables`
  //   instead of interpolating in `_t(...)` first:
  //   `replaceCustomContent('#{capitalizedWeeks} #{first} and #{last}', {`
  //   `  returnsString: true,`
  //   `  additionalVariables: { first: 1, last: 2 },`
  //   `})`
  // - The rule above only applies when using `_t(...)` interpolation. If you are using
  //   `FormattedMessage`, keep custom-label tokens as `#{...}` (e.g. `#{course}`) and keep
  //   regular message variables as `{...}` (e.g. `{supportLink}`), then pass those regular
  //   values as `FormattedMessage` props (not via `_t(...)` interpolation).
  //
  // NOTE: these function signatures must match `ReplaceCustomContent` in types/CustomLabels.ts.
  // They use the 'function' syntax over the arrow syntax to support effective method overloading.
  // Without the overload the conditional return type specified by ReplaceCustomContent
  // will not work and TypeScript will not know how to cast the return values.
  //
  // For more info see:
  // - https://stackoverflow.com/questions/56109614/typescript-overload-arrow-function-is-not-working
  // - https://stackoverflow.com/questions/55059436/typescript-conditional-return-type-based-on-string-argument
  function replaceCustomContent<T extends boolean>(
    message: string,
    options?: FormattingOptions<T>
  ): T extends true ? string : JSX.Element;
  function replaceCustomContent(message: string, formattingOptions?: FormattingOptions<boolean>) {
    const { additionalVariables = {}, weekNumber, returnsString = false } = formattingOptions ?? {};

    let labelOverrides = {};

    if (customLabels) {
      const { courseraCourse, courseraWeek } = customLabels.namings;

      const courseraCourseWithCapitalization = capitalizeEachWord(courseraCourse);
      const courseraWeekWithCapitalization = capitalizeEachWord(courseraWeek);

      // TODO(riwong): These exceptions might be better inside of getCustomWeekTerm(), I'm having trouble following the intended areas of maintenance
      let maybeWeeksOverride = {};
      if (courseraWeek === 'week') {
        // Special case if the courseraWeek is "week", hard-code the plural case
        maybeWeeksOverride = {
          weeks: _t('weeks'),
          capitalizedWeeks: capitalizeEachWord(_t('weeks')),
          // Also, make #{modules} and #{capitalizedModules} point to these same overrides,
          // as it is intentional for the caller to provide weeks/modules or Weeks/Modules,
          // and all of them should end up with the same output.
          modules: _t('weeks'),
          capitalizedModules: capitalizeEachWord(_t('weeks')),
        };
      } else if (courseraWeek && !isPluralSupportedWeekTerm(courseraWeek)) {
        // If there is a week override defined, and it's not one of the supported terms,
        // For the plural forms, just use the provided (most likely singular) form.
        maybeWeeksOverride = {
          weeks: courseraWeek,
          capitalizedWeeks: courseraWeekWithCapitalization,
          // Also, make #{modules} and #{capitalizedModules} point to these same overrides,
          // as it is intentional for the caller to provide weeks/modules or Weeks/Modules,
          // and all of them should end up with the same output.
          modules: courseraWeek,
          capitalizedModules: courseraWeekWithCapitalization,
        };
      }

      // There is no guarantee that the customLabelsV2 API will return labels for every
      // term, and since Object.assign() will brute copy over undefined values, we'll
      // use Lodash pickBy() to filter out falsy values. We could write our own helper method,
      // but seeing we have lodash at our disposal, we might as well use it.
      //
      // Read more:
      //   - https://github.com/lodash/lodash/issues/1093#issuecomment-87507761
      //   - https://stackoverflow.com/a/39977485/2466941
      //
      labelOverrides = pickBy({
        ...additionalVariables,
        ...maybeWeeksOverride,
        course: courseraCourse,
        week: courseraWeek,
        capitalizedCourse: courseraCourseWithCapitalization,
        capitalizedWeek: courseraWeekWithCapitalization,
      });
    }

    const variables = {
      ...additionalVariables,
      course: defaultCourseTerm,
      courses: defaultCoursesTerm,
      week: defaultWeekTerm,
      weeks: defaultWeeksTerm,
      module: defaultWeekTerm,
      modules: defaultWeeksTerm,
      capitalizedCourse: defaultCapitalizedCourseTerm,
      capitalizedCourses: defaultCapitalizedCoursesTerm,
      capitalizedWeek: defaultCapitalizedWeekTerm,
      capitalizedWeeks: defaultCapitalizedWeeksTerm,
      capitalizedModule: defaultCapitalizedWeekTerm,
      capitalizedModules: defaultCapitalizedWeeksTerm,
      ...labelOverrides,
      ...(weekNumber && getNumberedWeekTerms(weekNumber)),
    };

    if (returnsString) {
      return _t(message, variables);
    } else {
      return <FormattedMessage message={message} {...variables} />;
    }
  }

  return {
    replaceCustomContent,
  };
};
